import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import { concatSubjects } from "../../utils/dataTransform/ContentItem/library";
import { getDetailAnalytics } from "../../utils/analytics/library";
import { fetchDetailPageData } from "./initData";
import RelatedLinks from "./components/RelatedLinks/component";
import AuthorProfile from "./components/AuthorProfile/component";
import Ad from "../../components/Ad/component";
import NewsletterWidget from "../../components/NewsletterWidget/component";
import Byline from "./components/Byline/component";
import {
    isAdvertiserSponsored,
    isAdvertorial,
} from "../../utils/sponsor/library";
import MetaLabel from "./components/MetaLabel/component";
import { getPageIntro } from "../../utils/pageIntro/library";
import MaybeError from "../../components/MaybeError/component";
import DetailHeadline from "./components/DetailHeadline/component";
import DetailSummary from "./components/DetailSummary/component";
import Deck from "./components/Deck/component";
import ContentFeedback from "./components/ContentFeedback/component";
import FeedbackForm from "../../components/FeedbackForm/component";
import SponsorBy from "./components/SponsorBy/component";
import PageAnalytics from "../../components/PageAnalytics";
import { TYPE_DETAIL } from "../../utils/analytics/constants";
import { PAGE_COMPONENT_UNIVERSAL_CLASS_NAME } from "../../constants/page";
import Source from "./components/Source/component";
import Corrections from "./components/Corrections/component";
import Clarifications from "./components/Clarifications/component";
import RelatedSegments from "./components/RelatedSegments/component";
import { CONTENT_TYPES, SIDEBAR_ORDER, DISCOVERY_ORDER } from "./constants";
import Breadcrumbs from "./components/Breadcrumbs/component";
import useViafouraReset from "../../hooks/useViafouraReset";
import LeadMedia from "./components/LeadMedia/component";
import Discovery from "./components/Discovery/component";
import Story from "./components/Story/component";
import { BIO_AND_PHOTO, BIO } from "../../utils/author/constants";
import { withSuspensefulErrorBoundary } from "../../components/SuspensefulErrorBoundary/component";
import ErrorPage from "../ErrorPage/component";
import useRelatedItems from "./useRelatedItems";

const Detail = ({
    match,
    imageUrl,
    noAds = false,
    analyticsEnabled = true,
}) => {
    const dispatch = useDispatch();

    const { baseSection, subSection, contentId, path, pageType, statusCode } =
        useSelector((state) => state.app);

    const content = useSelector((state) => state.detail.content);
    const rightrail = useSelector((state) => state.rightRail);
    const relatedItems = content?.relatedItems;

    const {
        sponsorBio,
        byline,
        updatedAt,
        publishedAt,
        publishedAtVerbal,
        authorDisplay,
        authorList,
        source,
        newsletter,
        clarifications,
        corrections,
        description,
        headline,
        language,
        id,
        deck,
        intlinks,
        externalLinks,
        subject,
        adExclusions,
        gs_categories,
        gs_keywords,
    } = content;
    const shouldRenderStory = CONTENT_TYPES.includes(content?.type);
    //Only the first author's profile is rendered
    const authorData = authorList?.[0];
    const shouldDisplayAuthorProfile =
        authorData && [BIO_AND_PHOTO, BIO].includes(authorDisplay);

    const analyticsProps = getDetailAnalytics({ content });

    useRelatedItems(id);

    useEffect(() => {
        if (match.params?.contentId !== contentId) {
            dispatch(fetchDetailPageData(match.url, match.params));
        }
    }, [match.url, match.params?.section, match.params?.contentId]);

    // Handle Viafoura resetting context on story page navigation
    useViafouraReset(match.url);

    const classes = classNames(
        "detail",
        "detailBody",
        noAds ? "detailNoAds" : "",
        PAGE_COMPONENT_UNIVERSAL_CLASS_NAME
    );

    if (noAds) {
        return (
            <MaybeError
                statusCode={statusCode}
                polopolyId={contentId || match.params?.contentId}
            >
                <div className={classes} data-testid="detail">
                    <DetailHeadline text={headline} />
                    <DetailSummary text={description} />
                    {shouldRenderStory && <Story noAds={noAds} />}
                    {analyticsEnabled && (
                        <PageAnalytics
                            dtmData={analyticsProps}
                            type={TYPE_DETAIL}
                        />
                    )}
                </div>
            </MaybeError>
        );
    }

    const schemaContent = {
        "@context": "http://schema.org",
        "@type": "WebPage",
        speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: [".detailHeadline", ".detailSummary"],
        },
    };
    const schemaMarkup = { __html: JSON.stringify(schemaContent) };
    const pageIntro = getPageIntro({
        path,
        baseSection,
        subSection,
        pageType,
        imageUrl,
        rightrail,
        noHeading: true,
    });

    return (
        <MaybeError
            statusCode={statusCode}
            polopolyId={contentId || match.params?.contentId}
        >
            <div className={classes} data-testid="detail">
                {pageIntro}
                <div className="detailBodyContainer withSidebar">
                    <div className="withFlex">
                        <div
                            className="detailMainCol sclt-storycontent"
                            id="detailContent"
                            data-testid="detailMainColumn"
                        >
                            <Breadcrumbs renderLabel={MetaLabel} />
                            <DetailHeadline text={headline} lang={language} />
                            <DetailSummary text={description} />
                            <Deck text={deck} lang={language} />
                            {!isAdvertorial(sponsorBio?.label) && (
                                <Byline
                                    text={byline}
                                    updatedAt={updatedAt}
                                    publishedAt={publishedAt}
                                    publishedAtVerbal={publishedAtVerbal}
                                    authorDisplay={authorDisplay}
                                    authorList={authorList}
                                />
                            )}
                            {!isAdvertiserSponsored(sponsorBio) && (
                                <SponsorBy />
                            )}
                            {shouldRenderStory ? (
                                <Story noAds={noAds} />
                            ) : (
                                <LeadMedia />
                            )}
                            <RelatedSegments />
                            <Clarifications data={clarifications} />
                            <Corrections data={corrections} />
                            {shouldDisplayAuthorProfile && (
                                <AuthorProfile json={authorData} />
                            )}
                            <Source sourceInfo={source} />
                            <ContentFeedback
                                key={`ContentFeedback-${id}`}
                                headline={headline}
                                baseSection={baseSection}
                            />
                            <RelatedLinks
                                relatedItems={relatedItems}
                                intlinks={intlinks}
                            />
                            <RelatedLinks
                                externalLinks={externalLinks}
                                heading="External Links"
                            />
                            {newsletter && newsletter !== "false" && (
                                <NewsletterWidget
                                    promotenewsletter={newsletter}
                                />
                            )}
                        </div>
                    </div>
                    <div className="withFlex">
                        <div
                            className="detailSideRail"
                            data-testid="detailSideRail"
                        >
                            <div className="stickyContainer stickyRailAd">
                                <Ad
                                    type="bigbox-sticky"
                                    targeting={{
                                        type: "ocelot",
                                        contentId: contentId,
                                        rmtype: "bigboxfixed",
                                        subject: concatSubjects(subject),
                                        gs_categories: gs_categories,
                                        gs_keywords: gs_keywords,
                                        plc: "right",
                                    }}
                                    responsive
                                    lazy={true}
                                    exclusions={adExclusions}
                                />
                            </div>
                            <div className="stickyContainer">
                                <Discovery components={SIDEBAR_ORDER} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="detailBodyContainer">
                    <div className="withFlex">
                        <Discovery components={DISCOVERY_ORDER} />
                        <script
                            type="application/ld+json"
                            dangerouslySetInnerHTML={schemaMarkup}
                        ></script>
                    </div>
                </div>
                <FeedbackForm />
                {analyticsEnabled && (
                    <PageAnalytics
                        dtmData={analyticsProps}
                        type={TYPE_DETAIL}
                    />
                )}
            </div>
        </MaybeError>
    );
};

Detail.propTypes = {
    imageUrl: PropTypes.string,
    noAds: PropTypes.bool,
    analyticsEnabled: PropTypes.bool,
};

const SafeDetail = withSuspensefulErrorBoundary(Detail, {
    suspenseProps: {
        fallback: <ErrorPage statusCode={500} />,
    },
    errorBoundaryProps: { fallback: <ErrorPage statusCode={500} /> },
});

export default SafeDetail;
